<script setup lang="ts">
import { onBeforeMount, Ref, ref, toRefs } from 'vue';
import { useToast } from 'primevue/usetoast';
import { AssessmentMaterialData, AssessmentRecordFormData, DetailedEventInterface } from '@/types';
import { useTherapyStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

interface Props {
  event?: DetailedEventInterface;
  showAssessmentTitle?: boolean;
}

const props = defineProps<Props>();
const { event, showAssessmentTitle } = toRefs(props);
const toast = useToast();
const therapyStore = useTherapyStore();
const evaluationAssessmentDownloading = ref<boolean>(false);
const evaluationAssessmentsRecordForms: Ref<AssessmentRecordFormData[] | undefined> = ref([]);

onBeforeMount(async () => await init());

const init = async () => {
  if (can([PERMISSIONS.THERAPY_EVENT_STORE_EVALUATION_SELECT_ASSESSMENT])) {
    await therapyStore.getEvaluationAssessments();
    await setEvaluationAssessmentsRecordForms();
  }
};

const setEvaluationAssessmentsRecordForms = async () => {
  if (evaluationAssessmentsRecordForms.value) {
    let materials = event.value?.event_type_data.assessment_record_form.assessment_materials;
    const materialItems = materials
      .sort((a, b) => +(b.deleted_at === null) - +(a.deleted_at === null))
      .filter((item) => materials.find((value) => value.title === item.title && item.deleted_at === null))
      .sort((a, b) => a.title.localeCompare(b.title));

    event.value.event_type_data.assessment_record_form.assessment_materials = materialItems;

    evaluationAssessmentsRecordForms.value.push(event.value?.event_type_data.assessment_record_form);
  }
};

const onClickAssessmentRecordForm = async (recordForm: AssessmentRecordFormData) => {
  evaluationAssessmentDownloading.value = true;
  await therapyStore
    .downloadEvaluationAssessmentsRecordForm(recordForm)
    .finally(async () => {
      evaluationAssessmentDownloading.value = false;
    })
    .catch((errorResponse) => {
      evaluationAssessmentDownloading.value = false;

      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: errorResponse.data.message || errorResponse.statusText,
        life: 3000,
      });
    });
};

const onClickAssessmentMaterial = async (material: AssessmentMaterialData) => {
  evaluationAssessmentDownloading.value = true;
  await therapyStore
    .downloadEvaluationAssessmentsMaterial(material)
    .finally(async () => {
      evaluationAssessmentDownloading.value = false;
    })
    .catch((errorResponse) => {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: errorResponse.data.message || errorResponse.statusText,
        life: 3000,
      });
    });
};
</script>

<template>
  <div>
    <p v-if="showAssessmentTitle" class="font-bold">{{ evaluationAssessmentsRecordForms[0]?.assessment?.title }}</p>
    <ul class="flex flex-column justify-content-start align-items-start pl-0 list-none">
      <li v-for="form in evaluationAssessmentsRecordForms" :key="form.id" class="flex flex-column align-items-start">
        <Button
          class="p-button-link text-sm primary-color underline px-0 text-left"
          title="Record Form"
          :disabled="evaluationAssessmentDownloading"
          @click="onClickAssessmentRecordForm(form)"
        >
          Record Form {{ form.title }}
        </Button>

        <ul class="pl-0 list-none">
          <li v-for="material in form.assessment_materials" :key="material.id" class="flex align-items-start">
            <Button
              class="p-button-link text-sm primary-color underline px-0 text-left"
              title="Material"
              :disabled="evaluationAssessmentDownloading"
              @click="onClickAssessmentMaterial(material)"
            >
              {{ material.title }}
            </Button>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
